import { diasDoPeriodo, COR } from './Operacao.graficos'
import { formatarDia, Periodo } from './Dashboards.periodo'
import type { Negocios, PorFase } from './Dashboards.scopes'

export type Fase = keyof PorFase

export const FASES: readonly Fase[] = ['A', 'R', 'K', 'C']

export const ROTULO_FASE: Record<Fase | 'L', string> = {
    A: 'Abordagem',
    R: 'Reserva',
    K: 'Cota',
    C: 'Cliente',
    L: 'Livre ou perdido'
}

// A cor segue a fase em todos os gráficos. Ordem validada para daltonismo nos vizinhos (paleta de referência,
// slots 1 a 4); aqua e amarelo ficam abaixo de 3:1 no branco, então todo gráfico tem a tabela ao lado.
export const COR_FASE: Record<Fase, string> = {
    A: '#2a78d6',
    R: '#eb6834',
    K: '#1baf7a',
    C: '#eda100'
}

// Movimento: Clientes leva a cor da fase C; Derrubadas fica entre Reservas e Clientes para o laranja e o amarelo não
// ficarem vizinhos (validado).
export const COR_DERRUBADA = '#6250d6'

const numero = (n: number) => n.toLocaleString('pt-BR')

export function percentual(parte: number, total: number): string {
    if (!total) return '0%'
    const p = (parte / total) * 100
    return `${p < 10 ? p.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : Math.round(p)}%`
}

const LEGENDA = { top: 0, left: 0, icon: 'roundRect', itemWidth: 12, itemHeight: 12, textStyle: { color: COR.texto } }

/** Os maiores pipelines e o resto somado em "Outros". */
export function principaisPipelines(pipelines: Negocios['pipelines'], maximo = 8): Negocios['pipelines'] {
    if (pipelines.length <= maximo) return pipelines
    const principais = pipelines.slice(0, maximo - 1)
    const outros = pipelines
        .slice(maximo - 1)
        .reduce((soma, p) => ({ ...soma, A: soma.A + p.A, R: soma.R + p.R, K: soma.K + p.K, C: soma.C + p.C }), {
            pipeline: 'outros',
            nome: `Outros ${pipelines.length - maximo + 1} pipelines`,
            A: 0,
            R: 0,
            K: 0,
            C: 0
        })
    return [...principais, outros]
}

/** Carteira por pipeline: barras horizontais empilhadas por fase, o maior no topo. */
export function opcaoPipelines(pipelines: Negocios['pipelines']) {
    const linhas = [...principaisPipelines(pipelines)].reverse()
    return {
        grid: { left: 8, right: 24, top: 40, bottom: 8, containLabel: true },
        legend: LEGENDA,
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v: number) => numero(v) },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        yAxis: {
            type: 'category',
            data: linhas.map((p) => p.nome),
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto, width: 180, overflow: 'truncate' }
        },
        series: FASES.map((fase, i) => ({
            name: ROTULO_FASE[fase],
            type: 'bar',
            stack: 'carteira',
            barMaxWidth: 22,
            itemStyle: {
                color: COR_FASE[fase],
                borderColor: '#fff',
                borderWidth: 1,
                // Ponta arredondada só no fim da pilha.
                borderRadius: i === FASES.length - 1 ? [0, 4, 4, 0] : 0
            },
            data: linhas.map((p) => p[fase])
        }))
    }
}

/** Reservas e Cotas por gatilho, o mais comum no topo. Uma série: o título nomeia. */
export function opcaoGatilhos(gatilhos: Negocios['gatilhos'], maximo = 8) {
    const linhas = gatilhos.slice(0, maximo).reverse()
    return {
        grid: { left: 8, right: 48, top: 8, bottom: 8, containLabel: true },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: (v: number) => numero(v) },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        yAxis: {
            type: 'category',
            data: linhas.map((g) => g.gatilho),
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto, width: 220, overflow: 'truncate' }
        },
        series: [
            {
                name: 'Reservas e Cotas',
                type: 'bar',
                barMaxWidth: 20,
                itemStyle: { color: COR_FASE.R, borderRadius: [0, 4, 4, 0] },
                label: {
                    show: true,
                    position: 'right',
                    color: COR.texto,
                    formatter: (p: { value: number }) => numero(p.value)
                },
                data: linhas.map((g) => g.qtde)
            }
        ]
    }
}

/** Entradas em cada fase e derrubadas por dia, a partir do histórico. */
export function opcaoMovimento(movimento: Negocios['movimento'], periodo: Periodo) {
    const dias = diasDoPeriodo(periodo)
    const porDia = new Map(movimento.map((m) => [m.dia, m]))
    const linha = (name: string, cor: string, campo: (m: Negocios['movimento'][number]) => number) => ({
        name,
        type: 'line',
        showSymbol: false,
        symbolSize: 8,
        lineStyle: { width: 2, color: cor },
        itemStyle: { color: cor, borderColor: '#fff', borderWidth: 2 },
        data: dias.map((dia) => {
            const m = porDia.get(dia)
            return m ? campo(m) : 0
        })
    })
    return {
        grid: { left: 8, right: 16, top: 40, bottom: 8, containLabel: true },
        legend: LEGENDA,
        tooltip: { trigger: 'axis', valueFormatter: (v: number) => numero(v) },
        xAxis: {
            type: 'category',
            data: dias.map((d) => formatarDia(d)),
            axisLine: { lineStyle: { color: COR.eixo } },
            axisTick: { show: false },
            axisLabel: { color: COR.texto, hideOverlap: true }
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { color: COR.grade } },
            axisLabel: { color: COR.texto, formatter: (v: number) => numero(v) }
        },
        series: [
            linha('Entradas em Abordagem', COR_FASE.A, (m) => m.abordagens),
            linha('Entradas em Reserva ou Cota', COR_FASE.R, (m) => m.reservas),
            linha('Derrubadas', COR_DERRUBADA, (m) => m.derrubadas),
            linha('Viraram cliente', COR_FASE.C, (m) => m.clientes)
        ]
    }
}

/** Os negócios criados no período, pela fase de hoje: cada barra na cor da fase, com o percentual escrito. */
export function opcaoCriados(criados: Negocios['criados']) {
    const fases: (Fase | 'L')[] = ['A', 'R', 'K', 'C', 'L']
    const linhas = fases.map((fase) => ({ fase, qtde: criados[fase] })).reverse()
    return {
        grid: { left: 8, right: 96, top: 8, bottom: 8, containLabel: true },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            valueFormatter: (v: number) => `${numero(v)} (${percentual(v, criados.total)})`
        },
        xAxis: { type: 'value', show: false },
        yAxis: {
            type: 'category',
            data: linhas.map((l) => ROTULO_FASE[l.fase]),
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { color: COR.texto }
        },
        series: [
            {
                name: 'Negócios',
                type: 'bar',
                barMaxWidth: 22,
                label: {
                    show: true,
                    position: 'right',
                    color: COR.texto,
                    formatter: (p: { value: number }) => `${numero(p.value)} · ${percentual(p.value, criados.total)}`
                },
                data: linhas.map((l) => ({
                    value: l.qtde,
                    itemStyle: { color: l.fase === 'L' ? COR.eixo : COR_FASE[l.fase], borderRadius: [0, 4, 4, 0] }
                }))
            }
        ]
    }
}
